// Routage par hash — assez pour une SPA mono-utilisateur servie par un serveur
// qui n'a pas à connaître les routes de l'interface.
//
// DEUX CHOSES QUE CE FICHIER FAIT EN PLUS D'AIGUILLER, chacune pour une raison
// mesurée à l'usage :
//
//  1. LES FILTRES SONT DANS L'URL. Ils vivaient en état local : recharger la
//     page, ou simplement revenir d'une offre, remettait les sept filtres de la
//     veille à zéro. Sur une session de tri de 80 offres, c'est le geste qu'on
//     refait le plus souvent — et le seul qui ne laissait aucune trace.
//  2. LA POSITION DE LISTE EST MÉMORISÉE. Le code précédent faisait
//     `window.scrollTo(0, 0)` à CHAQUE changement de hash, retour vers la liste
//     compris : on repartait du haut après chaque offre traitée.

import { useEffect, useState } from 'react'

export type Route =
  | { name: 'decisions' }
  | { name: 'suivi' }
  | { name: 'veille' }
  | { name: 'offre'; id: number }
  | { name: 'vivier' }
  | { name: 'consultant'; id: number }
  /** « Mon profil » en Radar : SANS identifiant dans l'adresse. Un particulier
   *  n'a qu'un profil, et `#/consultant/22` lui montrait un numéro interne sous
   *  un mot qui ne le décrit pas. Le profil vient de la session. */
  | { name: 'profil' }
  | { name: 'sources' }
  | { name: 'config' }
  /** La page de prise en main (lot E) : le mode d'emploi, l'installation de
   *  l'extension et le canal de retour. Une route à part entière et non une
   *  modale d'accueil : elle doit rester atteignable pour toujours, c'est le
   *  premier endroit où l'on revient quand on a oublié comment faire. */
  | { name: 'aide' }
  /** Un lien d'invitation (issue #94). Le jeton vit après le `#` : le
   *  navigateur ne l'envoie jamais au serveur, donc il n'atterrit dans aucun
   *  journal. `App` l'affiche AVANT toute question de session. */
  | { name: 'invitation'; token: string }
  /** Le lien du mot de passe oublié (issue #96), après le `#` comme une
   *  invitation : jamais envoyé au serveur dans l'adresse. */
  | { name: 'connexion'; token: string }
  /** Le lien qui active un compte créé par e-mail (issue #182), après le `#`
   *  comme une invitation. */
  | { name: 'inscription'; token: string }
  /** L'inscription de Radar en trois écrans (lot 2 de la refonte). Page
   *  entière, sans navigation : on y arrive après l'invitation, ou depuis
   *  l'accueil d'un compte qui n'a pas encore déposé son CV. */
  | { name: 'bienvenue' }
  /** La fiche d'un employeur en Radar (lot 3) : ses offres dans vos métiers,
   *  et les autres, repliées avec une recherche. L'identifiant est la page. */
  | { name: 'employeur'; id: number }
  /** S'abonner à Radar (issue #109) : où mènent le bandeau de fin d'accès, le
   *  site et le retour de Stripe (`?caisse=ok|annulee`). */
  | { name: 'abonnement' }

/** `#/veille?tri=score` → `['#/veille', 'tri=score']`. */
function couper(hash: string): [string, string] {
  const i = hash.indexOf('?')
  return i === -1 ? [hash, ''] : [hash.slice(0, i), hash.slice(i + 1)]
}

export function parseHash(hash: string): Route {
  const [chemin] = couper(hash)
  const m = (re: RegExp) => re.exec(chemin)
  let r
  // Offre et dossier fusionnés le 25 août 2026 : le dossier s'affiche EN
  // PLACE dans la fiche offre (le candidat qui en a un l'a déjà sélectionné),
  // il n'a plus sa propre route. Un vieux lien vers un dossier précis reste
  // utile : il retombe sur l'offre plutôt que sur la pile du matin, l'id du
  // dossier lui-même est simplement ignoré.
  if ((r = m(/^#\/offre\/(\d+)\/dossier\/\d+$/))) return { name: 'offre', id: Number(r[1]) }
  if ((r = m(/^#\/offre\/(\d+)$/))) return { name: 'offre', id: Number(r[1]) }
  if ((r = m(/^#\/consultant\/(\d+)$/))) return { name: 'consultant', id: Number(r[1]) }
  if (m(/^#\/profil$/)) return { name: 'profil' }
  if (m(/^#\/vivier$/)) return { name: 'vivier' }
  if (m(/^#\/sources$/)) return { name: 'sources' }
  if ((r = m(/^#\/employeur\/(\d+)$/))) return { name: 'employeur', id: Number(r[1]) }
  if (m(/^#\/config$/)) return { name: 'config' }
  if (m(/^#\/aide$/)) return { name: 'aide' }
  if (m(/^#\/bienvenue$/)) return { name: 'bienvenue' }
  if (m(/^#\/abonnement$/)) return { name: 'abonnement' }
  if ((r = m(/^#\/invitation\/([A-Za-z0-9_-]+)$/))) return { name: 'invitation', token: r[1] }
  if ((r = m(/^#\/connexion\/([A-Za-z0-9_-]+)$/))) return { name: 'connexion', token: r[1] }
  if ((r = m(/^#\/inscription\/([A-Za-z0-9_-]+)$/))) return { name: 'inscription', token: r[1] }
  if (m(/^#\/suivi$/)) return { name: 'suivi' }
  if (m(/^#\/veille$/)) return { name: 'veille' }
  // Défaut : la pile du matin. C'est la page sur laquelle on ouvre la console
  // pour travailler ; la veille est devenue une page de consultation.
  return { name: 'decisions' }
}

export function parseQuery(hash: string): URLSearchParams {
  return new URLSearchParams(couper(hash)[1])
}

export function navigate(path: string): void {
  location.hash = path
}

/**
 * Réécrit les paramètres de la route COURANTE sans empiler d'entrée dans
 * l'historique. Voulu : sinon, régler six filtres puis cliquer « retour »
 * demanderait six clics pour sortir de l'écran.
 */
export function setQuery(params: URLSearchParams): void {
  const [chemin] = couper(location.hash)
  const q = params.toString()
  history.replaceState(null, '', q ? `${chemin || '#/decisions'}?${q}` : chemin || '#/decisions')
  // `replaceState` ne déclenche pas `hashchange` : on prévient nous-mêmes.
  window.dispatchEvent(new Event('routechange'))
}

// --- Mémoire des écrans de liste ---------------------------------------------

/**
 * Dernière URL complète (filtres compris) de chaque écran de liste, pour que
 * « Retour à la veille » ramène à la liste QU'ON REGARDAIT et non à une liste
 * vierge. En mémoire vive seulement : au rechargement, l'URL fait foi.
 */
const derniereListe = new Map<string, string>()
const positions = new Map<string, number>()

/** Le dernier écran de liste réellement affiché — `retourOffres` s'en sert. */
let derniereVisitee: string | null = null
/** Le dernier écran quitté qui n'était pas une offre, liste ou non. Une offre
 *  ouverte depuis la page d'un employeur ou depuis Mon CV y ramène : sans lui,
 *  « Retour » renvoyait à la dernière LISTE vue, parfois deux écrans plus tôt. */
let dernierEcran: string | null = null

type Liste = 'decisions' | 'suivi' | 'veille' | 'vivier' | 'sources'
const LISTES: string[] = ['decisions', 'suivi', 'veille', 'vivier', 'sources']
const EST_LISTE = (n: string): n is Liste => LISTES.includes(n)

export function hrefListe(nom: Liste): string {
  return derniereListe.get(nom) ?? `#/${nom}`
}

/**
 * D'où l'on vient parmi les trois écrans d'offres.
 *
 * « Retour » doit ramener à l'écran qu'on regardait, pas à un écran choisi une
 * fois pour toutes : on ouvre la même offre depuis la pile de décisions, depuis
 * le suivi ou depuis la veille, et renvoyer systématiquement vers la pile
 * ferait perdre sa recherche à qui consultait la veille.
 */
export function retourOffres(): { href: string; cle: CleRetour } {
  const avant = dernierEcran ? parseHash(dernierEcran).name : null
  if (avant === 'employeur') return { href: dernierEcran!, cle: 'offre.retourEmployeur' }
  if (avant === 'profil') return { href: '#/profil', cle: 'offre.retourProfil' }
  const noms = {
    decisions: 'offre.retourDecisions',
    suivi: 'offre.retourSuivi',
    veille: 'offre.retourVeille',
  } as const
  const nom = derniereVisitee && derniereVisitee in noms ? (derniereVisitee as keyof typeof noms) : 'decisions'
  // Une CLÉ de dictionnaire, plus le libellé fini : ce module ne sait rien de
  // la formule ni de la langue (il n'est pas un composant React, il n'a pas de
  // hook), et rendre du texte français en dur d'ici court-circuitait tout le
  // dispositif — c'était le dernier libellé de la fiche offre à ne pas
  // traduire, pendant que tout le reste de l'écran l'était.
  return { href: hrefListe(nom), cle: noms[nom] }
}

/** Les clés de `vocabulaire.ts` que `retourOffres` peut rendre. */
export type CleRetour =
  | 'offre.retourDecisions'
  | 'offre.retourSuivi'
  | 'offre.retourVeille'
  | 'offre.retourEmployeur'
  | 'offre.retourProfil'

const cle = (hash: string) => couper(hash)[0] || '#/decisions'

export function useRoute(): Route {
  const [hash, setHash] = useState(() => location.hash)

  useEffect(() => {
    let precedent = location.hash

    const onHashChange = () => {
      // Position de l'écran qu'on quitte, avant que le nouveau ne s'affiche.
      positions.set(cle(precedent), window.scrollY)
      const nom = parseHash(precedent).name
      if (EST_LISTE(nom)) {
        derniereListe.set(nom, precedent)
        derniereVisitee = nom
      }
      if (nom !== 'offre') dernierEcran = precedent
      precedent = location.hash
      setHash(location.hash)

      // Après le rendu du nouvel écran : avant, la page n'est pas encore assez
      // haute pour accepter le défilement demandé.
      const y = positions.get(cle(location.hash)) ?? 0
      requestAnimationFrame(() => window.scrollTo(0, y))
    }

    // `routechange` : changement de filtres, même écran — ni saut ni mémoire.
    const onQueryChange = () => setHash(location.hash)

    window.addEventListener('hashchange', onHashChange)
    window.addEventListener('routechange', onQueryChange)
    return () => {
      window.removeEventListener('hashchange', onHashChange)
      window.removeEventListener('routechange', onQueryChange)
    }
  }, [])

  return parseHash(hash)
}

/** Les paramètres de l'URL courante, et de quoi les réécrire. */
export function useQuery(): [URLSearchParams, (maj: Record<string, string | null>) => void] {
  const [hash, setHash] = useState(() => location.hash)

  useEffect(() => {
    const rafraichir = () => setHash(location.hash)
    window.addEventListener('hashchange', rafraichir)
    window.addEventListener('routechange', rafraichir)
    return () => {
      window.removeEventListener('hashchange', rafraichir)
      window.removeEventListener('routechange', rafraichir)
    }
  }, [])

  const ecrire = (maj: Record<string, string | null>) => {
    const suivant = parseQuery(location.hash)
    for (const [k, v] of Object.entries(maj)) {
      // `null` ou vide retire le paramètre : l'URL ne doit pas traîner
      // `entreprise=` quand le filtre est revenu à « toutes ».
      if (v === null || v === '') suivant.delete(k)
      else suivant.set(k, v)
    }
    setQuery(suivant)
  }

  return [parseQuery(hash), ecrire]
}
